import { GridColumnRequest, ReportGridRequest } from '@/api/services/common.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTableContent, StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.dto'
import { incidentsStatisticsDossierService } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.service'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { StatisticalAnalysisDossiersDatasourceResponsesDetailTypes, StatisticalAnalysisDossiersDatasourceResponsesTypes, StatisticalAnalysisDynamicObject, StatisticalAnalysisViewDetailTableGroupingColumns } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'
import { StatisticalAnalysisIncidentsDossierExternalInfo } from '@/modules/statistical-analysis-module/views/incidents-dossier/incidents-dossier.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { Nullable } from '@/shared/types/app.types'
import { AxiosResponse } from 'axios'
import { chain, extend, includes, reduce } from 'lodash'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { parseGroupingParams } from '@/modules/statistical-analysis-module/shared/helpers/helpers'

export class StatisticalAnalysisHandlerExportTableIncidentsDossier {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>
    protected selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[]
    protected columns: GridColumnRequest[]

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>, selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[], columns: GridColumnRequest[]) {
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
        this.selectedRows = selectedRows
        this.columns = columns
    }

    public async exportTo(exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisIncidentsDossierExternalInfo
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: []
        }

        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)}`,
            columns: this.columns,
            entityIdentifiersRequest
        }
        const groupFields = (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        const infoDetail = sidebarFilter.temporalFilter.id
        const infoType = sidebarFilter.typeTimeFilter.id

        const selectedRows = this.selectedRows as StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent[]
        const groupValueFilterRequests: StatisticalAnalysisDynamicObject[] = reduce(selectedRows, (aggr: StatisticalAnalysisDynamicObject[], value: StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent) => {
            const valueWithOmittedColumns = chain(value)
                .omitBy((_value: unknown, key: string) => key.startsWith('stats'))
                .omitBy((value: unknown) => value === null)
                .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                    return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
                }, {})
                .value()
            aggr.push(valueWithOmittedColumns)
            return aggr
        }, [])
        return incidentsStatisticsDossierService.detailExportTo(sidebarFilter, request, groupFields, infoDetail, infoType, groupValueFilterRequests, exportType)
    }

    public async exportExternal(row: StatisticalAnalysisDossiersDatasourceResponsesTypes, exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisIncidentsDossierExternalInfo
        const selectedRows = (this.selectedRows as StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTableContent[]).map((rowSelection: StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTableContent) => rowSelection.incidentId)
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: selectedRows
        }

        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)}`,
            columns: this.columns.filter((columnFiltered: GridColumnRequest) => {
                const columnsNotIncluded = ['notifiedPersonnel', 'notifiedResponsibilities']
                if (!includes(columnsNotIncluded, columnFiltered.field)) {
                    return columnFiltered
                }
            }),
            entityIdentifiersRequest
        }
        const infoDetail = sidebarFilter.temporalFilter.id
        const infoType = sidebarFilter.typeTimeFilter.id

        const groupValueFilterRequest: StatisticalAnalysisDynamicObject = chain(row)
            .omitBy((_value: unknown, key: string) => key.startsWith('stats'))
            .omitBy((value: unknown) => value === null)
            .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
            }, {})
            .value()
        return incidentsStatisticsDossierService.detailExternalExportTo(sidebarFilter, request, infoDetail, infoType, groupValueFilterRequest, exportType)
    }
}
